import { DatePicker } from "antd";
import { pipe, Option, ReadonlyArray, flow, Record } from "fp";

import * as Moment from "moment";
import React from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as GenericDriver from "~/Driver";
import * as Log from "~/Log";
import { Order } from "~/Order";
import { Time } from "~/Time";

export namespace Date {
  export type Props = Settings & { readonly driver?: GenericDriver.Driver };

  export const column = ({ driver, interval }: Props) => ({
    title: "Date",
    dataIndex: "dateTime",
    defaultSortOrder: "descend" as const,
    defaultFilteredValue: interval
      ? [Filter.Value.fromInterval({ interval })]
      : [],

    filterDropdown: (props: Filter.Props) => (
      <Filter interval={interval} driver={driver} {...props} />
    ),

    onFilter,
    sorter,
    render,
  });

  const sorter = (a: Log.Summary, b: Log.Summary): Order.Ordering =>
    pipe(Time.DateTime.compare(a.summary, b.summary), (dateTime) =>
      dateTime !== Order.equal()
        ? dateTime
        : GenericDriver.compare(a.summary.driver, b.summary.driver)
    );

  const render = (_: unknown, summary: Log.Summary) => (
    <Log.Summary.DateTime {...summary} />
  );

  const onFilter = (
    value: string | number | boolean,
    { summary }: Log.Summary
  ) =>
    pipe(
      Filter.Value.toInterval(value),
      Option.map(Time.Interval.Stopped.contains(summary)),
      Option.getOrElse(() => true)
    );

  export function Filter({
    driver,
    interval: initialInterval,
    confirm,
    clearFilters,
    selectedKeys,
    setSelectedKeys,
  }: Filter.Props) {
    const [clearings, updateClearings] = React.useState(0);
    const onClear = () => {
      // eslint-disable-next-line functional/no-expression-statement
      updateClearings(clearings + 1);

      // eslint-disable-next-line functional/no-expression-statement
      clearFilters?.();
    };

    const [interval, updateInterval] = React.useState(
      pipe(
        Option.fromNullable(initialInterval),
        Option.map((interval) => ({ interval })),
        Option.getOrElse(() =>
          Log.Summaries.Interval.initial(Time.DateTime.now(), driver)
        )
      )
    );

    const onConfirm = () => {
      // eslint-disable-next-line functional/no-expression-statement
      setSelectedKeys([Filter.Value.fromInterval(interval)]);

      // eslint-disable-next-line functional/no-expression-statement
      confirm();
    };

    return (
      <>
        <Filter.Controls>
          <Button
            type="link"
            size="small"
            disabled={!selectedKeys[0]}
            onClick={onClear}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={onConfirm}>
            OK
          </Button>
        </Filter.Controls>
        <Filter.Tags>
          {React.useMemo(
            () => (
              <Filter.Interval updateInterval={updateInterval} {...interval} />
            ),
            [clearings]
          )}
        </Filter.Tags>
      </>
    );
  }

  export namespace Filter {
    export type Props = Date.Props & {
      readonly confirm: () => void;
      readonly selectedKeys: readonly string[];
      readonly setSelectedKeys: (keys: readonly string[]) => void;
      readonly clearFilters?: () => void;
    };

    export const Controls = styled.div`
      display: flex;
      justify-content: space-between;
      padding: 8px;
      margin-bottom: 4px;
      border-bottom: 1px solid #f0f0f0;
    `;

    export const Tags = styled.div`
      padding: 0 4px 4px 4px;
    `;

    export type Value = string | number | boolean;
    export namespace Value {
      export const fromInterval = ({
        interval,
      }: Time.Interval.Stopped): string =>
        `${interval.start.dateTime},${interval.stop.dateTime}`;

      export const toInterval = (
        value: string | number | boolean
      ): Option<Time.Interval.Stopped> =>
        pipe(
          `${value}`.split(",") as readonly string[],
          ReadonlyArray.filterMap(
            flow(
              parseInt,
              Option.fromPredicate((dateTime) => !isNaN(dateTime)),
              Option.map(Time.DateTime.fromNumber)
            )
          ),
          Option.fromPredicate(
            (dateTimes): dateTimes is readonly [Time.DateTime, Time.DateTime] =>
              dateTimes[0] !== undefined && dateTimes[1] !== undefined
          ),
          Option.map((dateTimes) =>
            Time.Interval.Stopped.fromDateTimes(...dateTimes)
          )
        );
    }

    export const Interval = ({
      interval,
      updateInterval,
    }: Time.Interval.Stopped & {
      readonly updateInterval: (interval: Time.Interval.Stopped) => void;
    }) => {
      const initial = pipe(
        [interval.start, interval.stop],
        ReadonlyArray.map(({ dateTime }) => Moment.default(dateTime))
      );

      const onChange: React.ComponentProps<
        typeof DatePicker.RangePicker
      >["onChange"] = (moments) => {
        if (!moments || !moments[0] || !moments[1]) return;

        const [start, stop] = pipe(
          moments,
          ReadonlyArray.map((moment) =>
            Time.DateTime.fromNumber(
              Moment.default(moment).startOf("day").valueOf()
            )
          )
        );

        // eslint-disable-next-line functional/no-expression-statement
        updateInterval(Time.Interval.Stopped.fromDateTimes(start, stop));
      };

      return (
        <DatePicker.RangePicker
          bordered={false}
          allowClear={false}
          allowEmpty={[false, false]}
          defaultValue={initial as never}
          onCalendarChange={onChange}
        />
      );
    };
  }

  export type Settings = Partial<Time.Interval.Stopped>;
  export namespace Settings {
    export const encode = ({ interval }: Settings): readonly string[] =>
      pipe(
        Option.fromNullable(interval),
        Option.fold(
          () => [],
          flow(
            Record.map(Time.DateTime.toISOWithoutTime),
            ({ start, stop }) => [`start=${start}`, `stop=${stop}`]
          )
        )
      );

    export const parse = (
      getParameter: Log.Summaries.Table.Settings.GetParameter
    ): Settings =>
      pipe(
        Option.some(Time.Interval.Stopped.fromDateStrings),
        Option.ap(getParameter("start")),
        Option.ap(getParameter("stop")),
        Option.flatten,
        Option.getOrElse(() => ({}))
      );

    export const isEmpty = (settings: Settings): boolean =>
      settings.interval === undefined;
  }
}
